
import { Component, Vue } from 'vue-property-decorator';
import { getQuantListData, getAddressLookupData } from '@/api';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import { quantsConditionList } from '@/utils/PortalConstants';
import QuantCartWarnings from '@/components/quantCart/QuantCartWarnings.vue';
import { QuantList } from '@/models/jobQuants';
import { sortTableData } from '@/utils/Common';
import { mapState } from 'vuex';
import store from '@/store';
import { createJobForAdvancedBooking, createJobForWarehouse } from '@/helpers/booking';
import {
  AddressBookAirRoadStop,
  AddressBookEndpointParam,
  AddressBookLookupData,
  AddressBookResponse,
} from '@/models/addressBook';
import { User } from '@/models/main';
import { fetchGetAddressBookAddresses } from '@/helpers/fetchers';
import * as routeNames from '@/router/routeNames';
import { warehouseList } from '@/utils/PortalConstants';
import { formatDateStringWithTZ } from '@/utils/InputProcessingUtilities';
import { notifyNetworkError } from '@/helpers/notifiers';
import { LoadingLinkedQuantsError } from '@/helpers/throwables';

@Component({
  components: {
    BackButton,
    Button,
    QuantCartWarnings,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class QuantsCartPage extends Vue {
  public loading = false;
  public user!: User;

  public filterProduct = '';
  public filterCondition = '';
  public filterConditionList = quantsConditionList;
  public warehouseList = warehouseList;
  public formatDateStringWithTZ = formatDateStringWithTZ;

  private ricohWarehouseAddress: AddressBookLookupData = {
    city: null,
    line1: '',
    line2: null,
    line3: null,
    name: '',
    suburb: {
      id: 0,
      name: '',
      postcode: '',
      state: '',
    },
    id: 0,
    note_text: '',
    note_name: '',
    note_phone: '',
    warehouse: false,
  };
  public addressesWarehouses: AddressBookLookupData[] = [];

  public sortField = 'id';
  public sortDirection: 'asc' | 'desc' = 'asc';
  public tableColumns = [
    { field: 'id', label: 'ID', width: 'w-10', sortable: true },
    { field: 'condition', label: 'Condition', width: 'w-14', sortable: true },
    { field: 'product_id', label: 'Product', width: 'w-44', sortable: true },
    { field: 'qty', label: 'Qty', width: 'w-10', sortable: true },
    { field: 'warehouse_code', label: 'Warehouse', width: 'w-14', sortable: true },
    { field: 'reference', label: 'Reference', width: 'w-24', sortable: true },
    { field: 'reference2', label: 'Reference 2', width: 'w-28', sortable: true },
    { field: 'owner_id', label: 'Owner', width: 'w-16', sortable: true },
    { field: 'lot_id', label: 'Serial', width: 'w-28', sortable: true },
    { field: 'in_date', label: 'Date Added', width: 'w-40', sortable: true },
  ];
  public linkedQuants: Record<number, QuantList> = {};
  public loadingLinkedQuants = false;

  get cartCount(): number {
    return store.getters['moduleQuantCart/cartCount'];
  }

  get cartItemIds(): string {
    return store.getters['moduleQuantCart/cartItemIds'].join(', ');
  }

  get cartWarehouse(): string {
    return store.getters['moduleQuantCart/cartWarehouse'];
  }

  get hasLinkedQuants(): boolean {
    return store.getters['moduleQuantCart/hasLinkedQuants'];
  }

  get items(): QuantList[] {
    return store.getters['moduleQuantCart/cartItems'];
  }

  get totalCartCount(): number {
    const cartItemIds = this.items.map(i => i.id);
    const uniqueLinkedQuantIds = new Set(
      Object.values(this.linkedQuants)
        .filter(quant => !cartItemIds.includes(quant.id))
        .map(quant => quant.id),
    );
    const total = this.items.length + uniqueLinkedQuantIds.size;
    return total;
  }

  get linkedQuantsCount(): number {
    const cartItemIds = this.items.map(i => i.id);
    const groupedSiblingIds = new Set<number>();
    this.groupedItems.groupedAsLinked.forEach(siblings => {
      siblings.forEach(sibling => groupedSiblingIds.add(sibling.id));
    });

    const linkedCount = Object.values(this.linkedQuants).filter(
      quant => !cartItemIds.includes(quant.id) && !groupedSiblingIds.has(quant.id),
    ).length;

    let groupedSiblingsCount = 0;
    this.groupedItems.groupedAsLinked.forEach(siblings => {
      groupedSiblingsCount += siblings.length;
    });

    return groupedSiblingsCount + linkedCount;
  }

  get groupedItems() {
    const groups = new Map<number, QuantList[]>();
    const result = {
      mainItems: [] as QuantList[],
      groupedAsLinked: new Map<number, QuantList[]>(),
      groupedIds: [] as number[],
    };

    this.items.forEach(item => {
      if (item.link_quant_id && Array.isArray(item.link_quant_id)) {
        const linkId = Number(item.link_quant_id[0]);
        if (!groups.has(linkId)) {
          groups.set(linkId, []);
        }
        groups.get(linkId)!.push(item);
      }
    });

    const processedIds = new Set<number>();

    this.items.forEach(item => {
      if (processedIds.has(item.id)) {
        return;
      }

      const linkId = item.link_quant_id ? Number(item.link_quant_id[0]) : null;
      const group = linkId ? groups.get(linkId) : null;

      if (group && group.length > 1) {
        const mainItem = group.reduce((min, curr) => (curr.id < min.id ? curr : min));

        if (item.id === mainItem.id) {
          const siblings = group.filter(g => g.id !== item.id);
          result.mainItems.push(item);
          result.groupedAsLinked.set(item.id, siblings);
          result.groupedIds.push(...group.map(g => g.id));
          group.forEach(g => processedIds.add(g.id));
        }
      } else {
        result.mainItems.push(item);
        processedIds.add(item.id);
      }
    });

    return result;
  }

  public setSorting(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
  }

  public removeItem(id: number): void {
    this.$store.dispatch('moduleQuantCart/removeItem', id);
  }

  public getConditionColor(condition: string | null): string {
    if (!condition) {
      return 'grey';
    }
    const conditionMap: Record<string, string> = {
      new: 'green',
      excellent: 'teal',
      good: 'blue',
      used: 'orange',
      poor: 'red',
    };
    return conditionMap[condition.toLowerCase()] || 'grey';
  }

  get filteredItems(): QuantList[] {
    const filtered = this.groupedItems.mainItems.filter(item => {
      if (this.filterProduct && !item.product_id[1].toLowerCase().includes(this.filterProduct.toLowerCase())) {
        return false;
      }
      if (this.filterCondition && item.condition !== this.filterCondition) {
        return false;
      }
      return true;
    });
    return sortTableData(filtered, this.sortField as keyof QuantList, this.sortDirection);
  }

  public clearCart = () => {
    store.dispatch('moduleQuantCart/clearCart');
    this.linkedQuants = {};
  };

  private getAddressBookWrapper(customerCode: string): void {
    const params: AddressBookEndpointParam = {
      username: '',
      customer: customerCode,
      page: 1,
      warehouse: true,
    };
    fetchGetAddressBookAddresses(params).then((res: AddressBookResponse) => {
      this.addressesWarehouses = res.results.map(addr => ({
        ...addr,
        note_text: '',
        note_name: '',
        note_phone: '',
      }));
    });
  }

  public async bookWH2WHClicked() {
    const customerCode = String(this.items[0]?.owner_id?.[1] || this.user.customerCode);
    const curWhIdx = Object.values(this.warehouseList).findIndex(x => x === this.cartWarehouse);
    const chosenWhData = this.addressesWarehouses.find(x => x.name === Object.keys(this.warehouseList)[curWhIdx]);
    const cartItemIds = this.items.map(i => i.id);
    const uniqueLinkedQuants = Object.values(this.linkedQuants).filter(q => !cartItemIds.includes(q.id));
    const localStorageJobData = await createJobForWarehouse(
      [...this.items, ...uniqueLinkedQuants],
      customerCode,
      chosenWhData,
    );
    localStorageJobData.stops.length = 1;
    localStorage.setItem('jobDataWHToWH', JSON.stringify(localStorageJobData));
    this.$router.push({
      name: routeNames.WAREHOUSE_TO_WH_JB,
      query: { fromQuantPage: 'true' },
    });
  }

  public async bookWH2SWHClicked() {
    const customerCode = String(this.items[0]?.owner_id?.[1] || this.user.customerCode);
    const curWhIdx = Object.values(this.warehouseList).findIndex(x => x === this.cartWarehouse);
    const chosenWhData = this.addressesWarehouses.find(x => x.name === Object.keys(this.warehouseList)[curWhIdx]);
    const cartItemIds = this.items.map(i => i.id);
    const uniqueLinkedQuants = Object.values(this.linkedQuants).filter(q => !cartItemIds.includes(q.id));
    const localStorageJobData = await createJobForWarehouse(
      [...this.items, ...uniqueLinkedQuants],
      customerCode,
      chosenWhData,
    );
    localStorageJobData.stops.length = 1;
    localStorage.setItem('jobDataWHSameWH', JSON.stringify(localStorageJobData));
    this.$router.push({
      name: routeNames.WAREHOUSE_SAME_WH_JB,
      query: { fromQuantPage: 'true' },
    });
  }

  public async bookAdvancedJobClicked() {
    const customerCode = String(this.items[0]?.owner_id?.[1] || this.user.customerCode);

    const curWhIdx = Object.values(this.warehouseList).findIndex(x => x === this.cartWarehouse);
    const chosenWhData = this.addressesWarehouses.find(x => x.name === Object.keys(this.warehouseList)[curWhIdx]);

    const cartItemIds = this.items.map(i => i.id);
    const uniqueLinkedQuants = Object.values(this.linkedQuants).filter(q => !cartItemIds.includes(q.id));

    const job = await createJobForAdvancedBooking(
      [...this.items, ...uniqueLinkedQuants],
      chosenWhData,
      customerCode,
      this.ricohWarehouseAddress,
    );

    localStorage.setItem('jobData', JSON.stringify(job));
    this.$router.push({
      name: routeNames.ADVANCED_JOB_BOOKING,
      query: { fromQuantPage: 'true' },
    });
  }

  public hasLoadedLinkedQuants(item: QuantList): boolean {
    if (!item.link_quant_id) {
      return false;
    }
    const linkId = item.link_quant_id[0];
    return Object.values(this.linkedQuants).some(q => q.link_quant_id && q.link_quant_id[0] === linkId);
  }

  public hasGroupedSiblings(item: QuantList): boolean {
    return this.groupedItems.groupedAsLinked.has(item.id);
  }

  public getGroupedSiblings(item: QuantList): QuantList[] {
    return this.groupedItems.groupedAsLinked.get(item.id) || [];
  }

  public getAllLinkedQuants(item: QuantList): QuantList[] {
    if (!item.link_quant_id) {
      return [];
    }
    const linkGroupId = Number(item.link_quant_id[0]);
    const cartItemIds = this.items.map(i => i.id);

    const groupedSiblingIds = new Set<number>();
    this.groupedItems.groupedAsLinked.forEach(siblings => {
      siblings.forEach(sibling => groupedSiblingIds.add(sibling.id));
    });

    return Object.values(this.linkedQuants).filter(
      quant =>
        quant.link_quant_id &&
        Number(quant.link_quant_id[0]) === linkGroupId &&
        quant.id !== item.id &&
        !cartItemIds.includes(quant.id) &&
        !groupedSiblingIds.has(quant.id),
    );
  }

  async loadLinkedQuantsData(): Promise<void> {
    const itemsWithLinks = this.items.filter(item => item.link_quant_id);
    if (itemsWithLinks.length === 0) {
      return;
    }
    const uniqueLinkIds = Array.from(
      new Set(
        itemsWithLinks
          .filter(item => item.link_quant_id !== null && item.link_quant_id !== undefined)
          .map(item => Number(item.link_quant_id![0])),
      ),
    );
    if (uniqueLinkIds.length === 0) {
      return;
    }

    try {
      this.loadingLinkedQuants = true;
      const promises = uniqueLinkIds.map(linkId =>
        getQuantListData({
          page: 1,
          page_size: 100,
          customer_code: String(this.items[0]?.owner_id?.[1] || ''),
          in_date_gte: '',
          in_date_lte: '',
          link_quant_id: linkId,
        }),
      );

      const responses = await Promise.all(promises);
      const cartItemIds = this.items.map(i => i.id);

      responses.forEach(response => {
        if (response && response.results && response.results.length > 0) {
          response.results.forEach(quant => {
            if (!cartItemIds.includes(quant.id)) {
              this.$set(this.linkedQuants, quant.id, quant);
            }
          });
        }
      });
    } catch (error) {
      if (error instanceof LoadingLinkedQuantsError) {
        notifyNetworkError({ text: error?.message || 'Failed to load linked quants' });
      }
    } finally {
      this.loadingLinkedQuants = false;
    }
  }

  public async mounted() {
    this.getAddressBookWrapper(this.user.customerCode);
    if (this.user.customerCode === 'RICOH') {
      this.warehouseList['KK Ricoh'] = 'RIC';
      getAddressLookupData('RICOH').then((res: AddressBookLookupData[]) => {
        const wh = res.find(x => x.name === 'Ricoh Australia');
        if (wh) {
          this.ricohWarehouseAddress = wh;
        }
      });
    }
    this.loadLinkedQuantsData();
  }
}
